import { useCallback, DragEvent, MutableRefObject } from 'react';
import {
  isExternalFilesDrag,
  isOutsideElement,
  getDroppedItems,
} from 'helpers/externalDragAndDrop';
import { isInternalDataDrag } from 'helpers/internalDragAndDrop';
import { getRowFromEvent } from 'components/common/dataGrid/dom';
import {
  DocumentationViewItem,
  isDirectoryItemType,
} from 'store/documentation/model/types';

interface DropTarget {
  id: string;
  name: string | null;
}

interface UseExternalFilesDndParams {
  rows: DocumentationViewItem[];
  getRowId: (row: DocumentationViewItem) => string;
  getCurrentDropTarget: () => DropTarget;
  onFilesDrop: (
    files: File[],
    hasDirs: boolean,
    targetDirectoryId: string,
    emptyDirectoryPaths: string[]
  ) => void;
  dropTargetRef: MutableRefObject<DropTarget>;
  onDragStateChange: (isDragging: boolean) => void;
  onDropTargetChange: (name: string | null, id: string) => void;
}

export function useExternalFilesDnd({
  rows,
  getRowId,
  getCurrentDropTarget,
  onFilesDrop,
  dropTargetRef,
  onDragStateChange,
  onDropTargetChange,
}: UseExternalFilesDndParams): {
  containerProps: {
    onDragEnter: (e: DragEvent<HTMLDivElement>) => void;
    onDragOver: (e: DragEvent<HTMLDivElement>) => void;
    onDragLeave: (e: DragEvent<HTMLDivElement>) => void;
    onDrop: (e: DragEvent<HTMLDivElement>) => void;
  };
  rowProps: {
    onDragOver: (e: DragEvent<Element>) => void;
    onDragLeave: (e: DragEvent<Element>) => void;
    onDrop: (e: DragEvent<Element>) => void;
  };
} {
  const resetDropTarget = useCallback(() => {
    const { id, name } = getCurrentDropTarget();
    onDropTargetChange(name, id);
  }, [getCurrentDropTarget, onDropTargetChange]);

  const handleDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault();

      const targetDirectoryId = dropTargetRef.current.id;

      onDragStateChange(false);
      resetDropTarget();

      const { files, hasDirectories, emptyDirectoryPaths } =
        await getDroppedItems(e);

      if (!files.length && !emptyDirectoryPaths.length) {
        return;
      }

      onFilesDrop(files, hasDirectories, targetDirectoryId, emptyDirectoryPaths);
    },
    [dropTargetRef, onDragStateChange, onFilesDrop, resetDropTarget]
  );

  const onContainerDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!isExternalFilesDrag(e)) return;
      e.preventDefault();
      onDragStateChange(true);
      resetDropTarget();
    },
    [onDragStateChange, resetDropTarget]
  );

  const onContainerDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!isExternalFilesDrag(e)) return;
      e.preventDefault();
      resetDropTarget();
    },
    [resetDropTarget]
  );

  const onContainerDragLeave = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!isExternalFilesDrag(e)) return;
      if (!isOutsideElement(e, e.currentTarget)) {
        return;
      }
      onDragStateChange(false);
      resetDropTarget();
    },
    [onDragStateChange, resetDropTarget]
  );

  const onContainerDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!isExternalFilesDrag(e)) return;
      void handleDrop(e);
    },
    [handleDrop]
  );

  const onRowDragOver = useCallback(
    (e: DragEvent<Element>) => {
      if (!isExternalFilesDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      onDragStateChange(true);

      const row = getRowFromEvent<DocumentationViewItem>(e, rows, getRowId);

      if (!row || !isDirectoryItemType(row.type)) {
        resetDropTarget();
        return;
      }

      onDropTargetChange(row.name, row._id ?? getCurrentDropTarget().id);
    },
    [
      rows,
      getRowId,
      getCurrentDropTarget,
      onDragStateChange,
      onDropTargetChange,
      resetDropTarget,
    ]
  );

  const onRowDragLeave = useCallback(() => {}, []);

  const onRowDrop = useCallback(
    (e: DragEvent<Element>) => {
      if (!isExternalFilesDrag(e) || isInternalDataDrag(e)) return;
      e.stopPropagation();
      void handleDrop(e);
    },
    [handleDrop]
  );

  return {
    containerProps: {
      onDragEnter: onContainerDragEnter,
      onDragOver: onContainerDragOver,
      onDragLeave: onContainerDragLeave,
      onDrop: onContainerDrop,
    },
    rowProps: {
      onDragOver: onRowDragOver,
      onDragLeave: onRowDragLeave,
      onDrop: onRowDrop,
    },
  };
}
